///
/// EarthBrowser
///
/// Dataset Scene Layer
///
/// Manages scene objects for a specific feature type within a dataset
/// Each layer has its own container for independent visibility control
///
/// Created by Matt Giger on 1/2/2026
/// Copyright (c) 2026 Geodata Labs
///

import { Constants } from "@/constants"
import { Database } from '@/earthbrowser/database/Database'
import { pickLoadState, type LayerLoadState, type LoadPhase } from '@/earthbrowser/datasets/LoadState'
import type { TimeLaneDetail } from '@/earthbrowser/datasets/TimeLane'
import type { EarthBrowserContext } from '@/earthbrowser/EarthBrowserContext'
import { recordCrumb } from '@/earthbrowser/utils/CrashSentinel'
import { NodeBase } from '@stovokor/engine/NodeBase'
import { type RenderActionSet } from '@stovokor/engine/Renderer'
import { type RenderState } from '@stovokor/engine/RenderState'

const cChunkSize = 100				// Features built per frame for large sets
const cLargeSetThreshold = 500		// Sets above this are built in chunks

/**
 * Manifest structure from the dataset catalog
 */
export type DatasetManifest = {
	datasetId: string
	schemaVersion: 1
	latestVersion: string
	checkpoint: {
		version: string
		shards: Array<{
			key: string
			path: string
			url?: string
			rowCount: number
			sizeBytes: number
			sha256: string
			idRange?: { start: number, end: number }
		}>
	}
	deltas?: Array<{
		fromVersion: string
		toVersion: string
		upserts: { key: string, path: string, url?: string, rowCount: number, sizeBytes: number, sha256: string }
		deletes?: { key: string, path: string, url?: string, rowCount: number, sizeBytes: number, sha256: string }
	}>
}

export type DatasetDelta = NonNullable<DatasetManifest["deltas"]>[number]

/**
 * A single row parsed from an Arrow IPC shard. Field shapes vary per dataset,
 * so values are `unknown` and parsers convert explicitly (String/Number/Boolean).
 */
export type ArrowRow = Record<string, unknown>

/**
 * Base dataset feature interface
 *
 * Represents a feature in the dataset
 *
 * @template FeatureType - The type of the feature
 * @property id - The unique identifier of the feature
 */
export interface DatasetFeature {
	id: string | number
}

/**
 * Scene object that represents a feature in the 3D scene
 */
export interface DatasetSceneObject<FeatureType extends DatasetFeature> extends NodeBase {
	/** Reference to the source feature data */
	feature: FeatureType

	/** Update the scene object with new feature data */
	updateFeature?(feature: FeatureType): void
}

/**
 * Configuration for dataset sync behavior
 */
export interface DatasetSyncConfig {
	/** Dataset ID in the catalog (e.g., "earthquakes", "clouds") */
	datasetId: string

	/** Polling interval in milliseconds. 0 = no polling, only initial sync */
	pollIntervalMs: number

	/** Whether this layer should participate in catalog sync */
	needsSync: boolean

	/** Whether this layer supports incremental delta updates */
	supportsDelta: boolean

	/**
	 * When true with supportsDelta, skip downloading checkpoint shards; ingest only the delta.
	 * The service must publish a full replacement snapshot in each delta (e.g. earthquakes upserts.arrow).
	 */
	deltaOnlyIngest?: boolean

	/**
	 * Large reference dataset nothing on screen waits for at startup (cities
	 * + admin boundaries: ~25 MB and ~330k rows on a fresh install). The
	 * initial sync runs it after every other layer so its downloads and
	 * ingest don't delay the layers the user actually has enabled.
	 */
	deferInitialSync?: boolean
}

/**
 * Base Dataset Scene Layer
 *
 * Manages a collection of scene objects for a specific feature type.
 * Each layer has its own container node for visibility control.
 * Optionally participates in dataset sync via the DatasetLayerManager.
 */
export abstract class DatasetLayer<FeatureType extends DatasetFeature> extends NodeBase {
	protected readonly context: EarthBrowserContext
	public readonly name: string

	// Map of feature ID to scene object
	protected sceneObjects = new Map<string, DatasetSceneObject<FeatureType>>()

	/**
	 * Scene objects that survive refresh purges even when their feature is
	 * outside the layer's query window - used by loadSceneObjectById
	 * overrides for deep-link focus targets (see updateSceneObjects)
	 */
	protected pinnedSceneObjects = new Set<string>()

	/**
	 * Features received while the layer was hidden, keyed by id. Hidden
	 * layers build no scene objects: node construction is where the memory
	 * goes (markers, polygons, and GLTF models with their textures), and on
	 * memory-constrained mobile browsers building every layer's nodes at
	 * startup was enough to kill the tab with nothing visible. The set is
	 * built on the first render after the layer becomes visible.
	 */
	private deferredFeatures = new Map<string, FeatureType>()

	/**
	 * Active load sources keyed by who set them: "sync" (the manager, while
	 * downloading/ingesting), "build" (this class, while constructing a
	 * large set in chunks). Independent because a chunked build outlives
	 * the sync that triggered it. See getLoadState()
	 */
	private loadSources = new Map<string, LayerLoadState>()

	constructor(context: EarthBrowserContext, name: string) {
		super()
		this.context = context
		this.name = name
	}

	/**
	 * Sync configuration for this layer.
	 * Override to participate in dataset sync.
	 * Default: no sync needed.
	 */
	public getSyncConfig(): DatasetSyncConfig | null {
		return null
	}

	/**
	 * Time span of the data this layer can currently display (ms epoch),
	 * used by the timeline scrubber to show per-dataset availability.
	 * Override in time-based layers; null means not time-based / no data.
	 */
	public getAvailableTimeRange(): { startMs: number; endMs: number } | null {
		return null
	}

	/**
	 * Per-frame detail for the timeline: frame start times (for stepping)
	 * and loaded spans (for the buffered indicator). Null when the layer
	 * has no discrete frames to report.
	 */
	public getTimeLaneDetail(): TimeLaneDetail | null {
		return null
	}

	// ==================== Load State ====================

	/**
	 * Why this layer's picture is incomplete right now, or null when it is
	 * showing everything it has. Combines the manager-driven sync phases,
	 * the chunked scene-object build, and (for time-series layers that
	 * override frameLoadState) the frame the clock is waiting on. Polled by
	 * the UI through DatasetLayerManager.getLoadStates()
	 */
	public getLoadState(): LayerLoadState | null {
		return pickLoadState([...this.loadSources.values(), this.frameLoadState()])
	}

	/**
	 * Time-series layers override to report the frame the clock wants but
	 * which isn't loaded yet (see TextureSeries.loadState)
	 */
	protected frameLoadState(): LayerLoadState | null {
		return null
	}

	/**
	 * Enter or update a load phase for `source`. The phase start time is
	 * kept across updates within the same phase so the UI's hysteresis sees
	 * one continuous wait, not a restart on every progress tick
	 */
	public setLoadPhase(source: string, phase: LoadPhase, extra: Omit<LayerLoadState, "phase" | "since"> = {}): void {
		const previous = this.loadSources.get(source)
		const since = previous?.phase === phase ? previous.since : performance.now()
		this.loadSources.set(source, { phase, since, ...extra })
	}

	/** Leave whatever phase `source` was in */
	public clearLoadPhase(source: string): void {
		this.loadSources.delete(source)
	}

	/**
	 * Create the database table for this layer.
	 * Called during initial setup before any sync.
	 */
	public async createTable(_db: Database): Promise<void> {
		// Override in subclass if needed
	}

	/**
	 * Apply checkpoint data from manifest.
	 * Called by DatasetLayerManager when new data is available.
	 *
	 * @param manifest - The manifest with checkpoint data
	 * @param downloadArrowRows - Function to download and parse Arrow IPC data
	 */
	public async applyCheckpoint(
		_manifest: DatasetManifest,
		_downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		// Override in subclass
	}

	/**
	 * Apply delta update from manifest.
	 * Called by DatasetLayerManager for incremental updates.
	 *
	 * @param delta - The delta with upserts and optionally deletes
	 * @param downloadArrowRows - Function to download and parse Arrow IPC data
	 */
	public async applyDelta(
		_delta: DatasetDelta,
		_downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void> {
		// Override in subclass if supportsDelta is true
	}

	/**
	 * Called after sync completes (checkpoint or delta applied).
	 * Use this to refresh scene objects or notify listeners.
	 */
	public async onSyncComplete(): Promise<void> {
		// Override in subclass
	}

	/**
	 * Return the number of rows currently stored in the database for a dataset.
	 * Used by the DatasetLayerManager to verify that a sync actually landed data,
	 * and to self-heal datasets that were marked applied while their table is empty.
	 * Return null when row counting isn't supported (verification is skipped).
	 *
	 * @param datasetId - The dataset ID (primary or sub-dataset)
	 */
	public async getStoredRowCount(_datasetId: string): Promise<number | null> {
		return null
	}

	/**
	 * Return additional dataset IDs that this layer manages.
	 * Used for layers that sync multiple related datasets (e.g., locations).
	 * Override in subclass if layer manages multiple datasets.
	 */
	public getSubDatasetIds?(): readonly string[]

	/**
	 * Apply checkpoint for a sub-dataset.
	 * Called by DatasetLayerManager for layers with sub-datasets.
	 *
	 * @param datasetId - The sub-dataset ID being synced
	 * @param manifest - The manifest with checkpoint data
	 * @param downloadArrowRows - Function to download and parse Arrow IPC data
	 */
	public applySubDatasetCheckpoint?(
		datasetId: string,
		manifest: DatasetManifest,
		downloadArrowRows: (url: string) => Promise<ArrowRow[]>
	): Promise<void>

	/**
	 * Resolve a shard/delta path to a full URL
	 */
	protected resolveUrl(pathOrUrl: string | undefined, path: string): string {
		return pathOrUrl ?? `${Constants.baseStorageUrl}/${path}`
	}

	// ==================== Scene Object Management ====================

	public async queryFeatures(_db: Database): Promise<FeatureType[]> {
		return []
	}

	/**
	 * Update scene objects based on features
	 * For large feature sets, processes in chunks to avoid blocking the main thread
	 */
	public updateSceneObjects(features: FeatureType[]): void {
		const existingIds = new Set(this.sceneObjects.keys())
		const newIds = new Set(features.map(f => String(f.id)))

		// Remove scene objects for features that no longer exist, except
		// pinned ones: on-demand loads (deep-link focus targets) that fall
		// outside the layer's rolling query window (e.g. earthquakes keep
		// only the latest 1000) must survive refreshes or the focused
		// entity would vanish mid-view
		for (const id of existingIds) {
			if (!newIds.has(id) && !this.pinnedSceneObjects.has(id)) {
				this.removeSceneObject(id)
			}
		}

		// A newer feature set supersedes anything still waiting to be built
		this.deferredFeatures.clear()

		if (!this.visible) {
			// Hidden: keep already-built nodes current (cheap) but don't
			// construct new ones until the layer is shown
			for (const feature of features) {
				const id = String(feature.id)
				const existing = this.sceneObjects.get(id)
				if (existing) {
					existing.updateFeature?.(feature)
				} else {
					this.deferredFeatures.set(id, feature)
				}
			}
			if (this.deferredFeatures.size > 0) {
				recordCrumb(`layer:${this.name} deferred n=${this.deferredFeatures.size}`)
			}
			return
		}

		this.buildFeatures(features)
	}

	/**
	 * Construct or update scene objects for a feature set: synchronously for
	 * small sets, chunked across frames for large ones
	 */
	private buildFeatures(features: FeatureType[]): void {
		if (features.length <= cLargeSetThreshold) {
			for (const feature of features) {
				this.processFeature(feature)
			}
			if (features.length > 0) {
				console.log(`[DatasetLayer] ${this.name}: ${features.length} scene objects updated`)
				recordCrumb(`layer:${this.name} updated n=${features.length}`)
			}
			return
		}

		console.log(`[DatasetLayer] ${this.name}: processing ${features.length} features in chunks of ${cChunkSize}`)
		recordCrumb(`layer:${this.name} build-start n=${features.length}`)

		// Note: Not awaiting intentionally - allows sync to complete while scene updates asynchronously
		// Scene updates happen in background without blocking sync completion
		void this.processFeaturesInChunks(features, cChunkSize)
	}

	/**
	 * Build any features deferred while hidden. Runs from render, which only
	 * executes while the layer is visible, so no visibility hook is needed
	 * in the subclasses that each manage their own redux subscription.
	 */
	override render(state: RenderState): RenderActionSet[] {
		if (this.visible && this.deferredFeatures.size > 0) {
			const features = [...this.deferredFeatures.values()]
			this.deferredFeatures.clear()
			this.buildFeatures(features)
		}
		return super.render(state)
	}

	/**
	 * Process a single feature (extract from updateSceneObjects for reuse)
	 */
	private processFeature(feature: FeatureType): void {
		const id = String(feature.id)
		const existing = this.sceneObjects.get(id)

		if (existing) {
			// Update existing scene object
			existing.updateFeature?.(feature)
		} else {
			// Create new scene object
			this.addSceneObject(feature)
		}
	}

	/**
	 * Process features in chunks using requestAnimationFrame to avoid blocking
	 */
	private async processFeaturesInChunks(features: FeatureType[], chunkSize: number): Promise<void> {
		let processed = 0
		const total = features.length
		const formatCount = (n: number) => n.toLocaleString("en-US")
		this.setLoadPhase("build", "building", { progress: 0, detail: `0 / ${formatCount(total)}` })

		while (processed < total) {
			// Layer hidden mid-build: park the remainder for the next show
			// instead of spending memory on nodes nothing will draw
			if (!this.visible) {
				for (const feature of features.slice(processed)) {
					this.deferredFeatures.set(String(feature.id), feature)
				}
				recordCrumb(`layer:${this.name} build-paused ${processed}/${total}`)
				this.clearLoadPhase("build")
				return
			}

			const chunk = features.slice(processed, processed + chunkSize)

			// Process this chunk
			for (const feature of chunk) {
				this.processFeature(feature)
			}

			processed += chunk.length
			this.setLoadPhase("build", "building", {
				progress: processed / total,
				detail: `${formatCount(processed)} / ${formatCount(total)}`,
			})

			// Progress log: when a memory-constrained tab dies mid-build, the
			// last chunk logged narrows the kill to a 200-feature window
			if (processed % 200 === 0 || processed >= total) {
				recordCrumb(`layer:${this.name} ${processed}/${total}`)
			}

			// Yield to browser to keep frames flowing
			await new Promise(resolve => requestAnimationFrame(() => resolve(undefined)))
		}
		this.clearLoadPhase("build")
	}

	/**
	 * Refresh the layer - reload from database and update scene objects
	 */
	public async refresh(): Promise<void> {
		const db = Database.getDb()
		if (!db) return

		const features = await this.queryFeatures(db)
		this.updateSceneObjects(features)
	}

	/**
	 * Create a scene object for a feature
	 */
	abstract createSceneObject(_feature: FeatureType): DatasetSceneObject<FeatureType> | undefined

	/**
	 * Add a scene object for a feature
	 */
	protected addSceneObject(feature: FeatureType): void {
		const id = String(feature.id)

		try {
			const sceneObject = this.createSceneObject(feature)
			if (!sceneObject) return
			this.sceneObjects.set(id, sceneObject)
			this.addNode(sceneObject)
		} catch (error) {
			console.error(`[DatasetLayer] Error creating scene object for ${id}:`, error)
		}
	}

	/**
	 * Remove a scene object
	 */
	protected removeSceneObject(id: string): void {
		const sceneObject = this.sceneObjects.get(id)
		if (!sceneObject) return

		this.removeNode(sceneObject)
		sceneObject.destroy()

		this.sceneObjects.delete(id)
	}

	/**
	 * Get all scene objects in this layer
	 */
	getSceneObjects(): DatasetSceneObject<FeatureType>[] {
		return Array.from(this.sceneObjects.values())
	}

	/**
	 * Get a scene object by ID
	 */
	getSceneObject(id: string): DatasetSceneObject<FeatureType> | undefined {
		const existing = this.sceneObjects.get(id)
		if (existing) return existing

		// Hidden layers build nothing up front, but a focus request (search,
		// deep link, notification) still needs its node: build just that one
		const deferred = this.deferredFeatures.get(id)
		if (deferred) {
			this.deferredFeatures.delete(id)
			this.addSceneObject(deferred)
			return this.sceneObjects.get(id)
		}
		return undefined
	}

	/**
	 * Load a scene object by ID from the database on-demand.
	 * Used when the scene object isn't currently loaded (e.g., viewport-based layers).
	 * Override in subclass to support on-demand loading.
	 *
	 * @param id - The feature ID to load
	 * @returns The scene object if found and loaded, undefined otherwise
	 */
	async loadSceneObjectById(_id: string): Promise<DatasetSceneObject<FeatureType> | undefined> {
		// Default implementation returns undefined - subclasses can override
		return undefined
	}

	/**
	 * Get the number of scene objects in this layer
	 */
	getCount(): number {
		return this.sceneObjects.size
	}

	/**
	 * Clear all scene objects in this layer
	 */
	clear(): void {
		this.deferredFeatures.clear()
		this.loadSources.delete("build")
		for (const id of this.sceneObjects.keys()) {
			this.removeSceneObject(id)
		}
	}

	/**
	 * Dispose the layer and all its scene objects
	 */
	dispose(): void {
		this.clear()
		this.removeAllNodes()
	}
}
